import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { LookupCombo } from '../../../components/LookupCombo';
import FormLink from '../../../components/FormLink';
import { ViewToggle } from '../../../components/ViewToggle';
import { ICM_LIST_TAKE, icmTruncationNote } from '../icmPaging';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useConfirm } from '../../../components/confirm';
import { useAuthStore } from '../../../auth/store';
import { fmtLocalDate, fmtLocalShort, toWire } from '../../../utils/datetime';
import { downloadBlob } from '../../../utils/exportData';
import { openLabelReport } from '../tracking/icmTrackingApi';
import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';

interface ItemInitRow {
  oid: string;
  esid: string | null;
  lt: string | null;
  itemName: string | null;
  storageName: string | null;
  givenToName: string | null;
  totalItems: number;
  expiryDate: string | null;
  vendorLt: string | null;
  statusLabel: string;
  initializedDate: string | null;
  initializedByName: string | null;
  /* Legacy list columns. */
  vendorName: string | null;
  catalog: string | null;
  description: string | null;
  departmentName: string | null;
  fileDataOid: string | null;
}

/** One certificate PDF on the lot's VendorReagentCertificate — the same shape
 *  Vendors COA uses, because it is the same endpoint. */
interface VendorCoaAttachment {
  oid: string;
  fileDataOid: string | null;
  fileName: string | null;
  sizeBytes: number;
}

interface Option { id: string; name: string; }

/** An item carries the three fields legacy's detail shows READ-ONLY beside it
 *  (Vendor = Item.Vendor.Vendor, Description = Item.Specification,
 *  Catalog# = Item.VendorCatName), so picking an item fills them in. */
interface ItemOpt extends Option {
  catalog: string | null;
  description: string | null;
  vendorId: string | null;
  vendorName: string | null;
}

/* Total Items and Given To were dropped from this form on 2026-09-29 (owner
   request); neither is on legacy's detail view.
   . Order Qty IS the quantity -- the save sends it as TotalItems, which is what
     drives itemremaining and the item's stock.
   . Given To is not a field of this screen at all: legacy's initialization
     writes Storage OR givento, never both, and the Storage path (the one AI
     takes, Status 1 PendingConsume) leaves it NULL -- as 8,382 of the 8,383
     live Distribution rows do. */
interface InitForm {
  itemId: string;
  storageId: string;
  departmentId: string;
  orderQty: number | '';
  receiveDate: string;
  expiryDate: string;
  vendorLt: string;
  comment: string;
}

/** Server-held values the detail shows but never edits — legacy marks each of
 *  these AllowEdit="False" (LT carries NullText "Auto generate"). */
interface InitReadOnly {
  lt: string | null;
  statusLabel: string;
  initializedDate: string | null;
  initializedByName: string | null;
  vendorName: string | null;
  description: string | null;
  catalog: string | null;
}

const EMPTY_FORM: InitForm = {
  itemId: '', storageId: '', departmentId: '',
  orderQty: 1, receiveDate: '', expiryDate: '', vendorLt: '', comment: '',
};

/** A NEW record's read-only side. Legacy pre-fills Initialized Date and
 *  Initialized By on its New form (they are what the server will stamp), so
 *  they are shown rather than left blank -- `meName` is filled in by the page. */
function emptyRo(meName: string): InitReadOnly {
  return {
    lt: null, statusLabel: 'Pending Consume',
    // toWire, not toISOString: with the ADR-0008 kill-switch on, a value is a
    // NAIVE server-local wall-clock and the fmtLocal* helpers render it
    // literally -- so a UTC string here displayed as the UTC time (12:42 for a
    // local 18:12), which is not what the server will stamp.
    initializedDate: toWire(new Date()),
    initializedByName: meName || null,
    vendorName: null, description: null, catalog: null,
  };
}

export default function ItemInitListPage() {
  const notify = useNotify();
  const saveFlow = useSaveFlow();
  const { confirm } = useConfirm();
  const meName = useAuthStore(st => st.user?.name || st.user?.email || '');

  const [rows, setRows] = useState<ItemInitRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  /* Detail-view state. `detail` non-null REPLACES the list with the full-page
     form (standing rule 12) — it used to be a 480px popup panel below the grid,
     which is neither the shared detail anatomy nor legacy's layout. */
  /* 'view' is the READ-ONLY landing mode a row click opens, matching GoLive
     (its detail URL carries &mode=View and offers Edit) and the shared
     <SettingsDetailPage>, whose `viewOnly` branch renders the same way
     (owner 2026-09-30: "without click the edit, then the detail view should
     opened for view and edit"). The row Edit pencil and the toolbar Edit go
     straight to 'edit'; New is always 'edit'. */
  const [detail, setDetail] = useState<{ mode: 'new' | 'edit' | 'view'; id?: string } | null>(null);

  /* ── The detail's COA tab (owner 2026-09-30: "when edit the detail view this
     attachment was missed"). Legacy's Distribution_DetailView_ItemInitialization
     carries a COA tab whose child grid lists the lot's
     VendorReagentCertificate.Upload files with New · Delete · Download.
     ⚠️ NO NEW API: those files already have list / upload / delete endpoints on
     Vendors COA (/vendor-coas/{certId}/attachments), because it is the SAME
     VendorReagentCertificate. All this form needed was the lot's certificate id,
     which AI_ICM_ItemInit_Get_SP now returns (standing rule 8). */
  const [coaFiles, setCoaFiles] = useState<VendorCoaAttachment[]>([]);
  const [coaBusy, setCoaBusy]   = useState(false);
  /** Ticked rows in the COA grid — the toolbar Delete and Download act on these,
   *  which is how legacy's child bar works (its buttons are selection-driven). */
  const [coaSel, setCoaSel]     = useState<Set<string>>(new Set());
  /** <AppDataGrid> is generic over string|number ids; this form's are Guid
   *  strings, so the selection is narrowed once here rather than widening every
   *  consumer of `coaSel`. */
  const setCoaSelFromGrid = (ids: Set<string | number>) =>
    setCoaSel(new Set([...ids].map(String)));
  const [certId, setCertId]     = useState<string | null>(null);

  const loadCoaFiles = useCallback(async (cert: string | null) => {
    setCoaSel(new Set());
    if (!cert) { setCoaFiles([]); return; }
    try {
      const res = await api.get(`/api/v1/vendor-coas/${cert}/attachments`);
      setCoaFiles((res.data as VendorCoaAttachment[]) ?? []);
    } catch { setCoaFiles([]); }
  }, []);
  const [form, setForm] = useState<InitForm>(EMPTY_FORM);
  const [ro, setRo] = useState<InitReadOnly>(() => emptyRo(''));
  const [saving, setSaving] = useState(false);
  /* The form exactly as the server last handed it over -- Refresh compares
     against this to decide whether it must ask before discarding edits. */
  const loadedForm = useRef<InitForm>(EMPTY_FORM);
  const [items, setItems] = useState<ItemOpt[]>([]);
  const [storageOptions, setStorageOptions] = useState<Option[]>([]);
  const [departments, setDepartments] = useState<Option[]>([]);

  /** Legacy's Import action. The server parses the workbook, resolves the names
   *  and writes the lots; it reports every bad row at once and commits nothing
   *  when any row fails, where legacy stops at the first one. */
  async function importFile(fileList: FileList | null) {
    const file = fileList?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setImporting(true);
    try {
      const res = await api.post('/api/v1/item-init/import', fd);
      const { created = 0 } = (res.data ?? {}) as { created?: number };
      notify.success(`${created} LT${created === 1 ? '' : 's'} generated successfully.`);
      await load();
    } catch (err) {
      const data = (err as { response?: { data?: { message?: string; errors?: string[] } } })?.response?.data;
      const msg = data?.errors?.length ? data.errors.slice(0, 6).join('; ') : data?.message;
      notify.error(msg || 'Import failed.');
    } finally { setImporting(false); }
  }

  /** Download Template and the COA cell BOTH used to be plain <a href> links.
   *  They 401: the API is [Authorize]d and reads the JWT from the Authorization
   *  HEADER, which a browser-initiated navigation cannot send (there is no
   *  cookie to fall back on). The shared axios client carries the header, so
   *  both now fetch a blob and hand it to the shared downloadBlob -- the same
   *  correction Vendors COA already records for its certificate downloads.
   *  Fixed 2026-09-29 while auditing that every toolbar control works. */
  async function downloadTemplate() {
    try {
      const res = await api.get('/api/v1/item-init/template', { responseType: 'blob' });
      downloadBlob(res.data, 'ItemInitializationImport.xlsx');
    } catch {
      notify.error('Failed to download the template.');
    }
  }

  /** QRLabel: the LT barcode PDF. Reuses LT# COA Tracking's generator through
   *  its existing endpoint (see the column's note) -- one lot at a time here,
   *  which is what legacy's per-row action does. */
  /* COA tab actions. All three go through the EXISTING Vendors COA endpoints
     (see the state note above): the files belong to the lot's
     VendorReagentCertificate, which is the very record Vendors COA manages. */
  async function uploadCoaFile(file: File) {
    /* The lot may not have a certificate yet, in which case there is nothing to
       attach to and New did nothing (owner 2026-09-30: "New button is not
       working"). Legacy creates the VendorReagentCertificate during
       SaveInitialization for every container
       (VendorReagentCertificateInitialization, a find-or-create keyed on
       VendorLT); AI never did, so EVERY initialized lot carried a NULL FK.
       The save now does it, and this covers the lots saved before it learned
       to -- one call, idempotent, at the moment the user actually attaches a
       file, rather than a blanket backfill over a regulated table. */
    let cert = certId;
    setCoaBusy(true);
    try {
      if (!cert) {
        if (!detail?.id) { notify.warning('Save this lot before attaching a certificate.'); return; }
        const res = await api.post(`/api/v1/item-init/${detail.id}/certificate`);
        cert = (res.data as { certId?: string } | undefined)?.certId ?? null;
        if (!cert) { notify.warning('This lot has no Vendor Lot, so it cannot have a vendor certificate.'); return; }
        setCertId(cert);
      }
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/api/v1/vendor-coas/${cert}/attachments`, fd);
      await loadCoaFiles(cert);
      notify.success('Uploaded successfully.');
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      notify.error(msg ?? 'Failed to upload that certificate.');
    } finally { setCoaBusy(false); }
  }

  /** Toolbar Delete — acts on the TICKED rows, as legacy's child bar does.
   *  Sequential rather than one call: the endpoint deletes one upload at a
   *  time, and a partial failure then leaves the rest of the set intact. */
  async function deleteCoaSelection() {
    if (!certId || coaSel.size === 0) { notify.warning('Select a certificate to delete.'); return; }
    const picked = coaFiles.filter(f => coaSel.has(f.oid));
    if (!(await confirm({
      title: 'Delete?',
      message: picked.length === 1
        ? `Remove ${picked[0]!.fileName ?? 'this file'} from the certificate?`
        : `Remove ${picked.length} files from the certificate?`,
      tone: 'danger',
    }))) return;
    setCoaBusy(true);
    try {
      for (const f of picked) await api.delete(`/api/v1/vendor-coas/${certId}/attachments/${f.oid}`);
      setCoaSel(new Set());
      await loadCoaFiles(certId);
      notify.success('Deleted.');
    } catch {
      notify.error('Failed to remove that certificate.');
    } finally { setCoaBusy(false); }
  }

  /** Toolbar Download — the ticked rows, one file each (legacy downloads the
   *  single selected row; several ticked simply yields several files). */
  async function downloadCoaSelection() {
    if (coaSel.size === 0) { notify.warning('Select a certificate to download.'); return; }
    for (const f of coaFiles.filter(x => coaSel.has(x.oid))) await downloadCoaFile(f);
  }

  async function downloadCoaFile(row: VendorCoaAttachment) {
    if (!row.fileDataOid) return;
    try {
      const res = await api.get(`/api/v1/item-init/file/${row.fileDataOid}/download`, { responseType: 'blob' });
      downloadBlob(res.data, row.fileName ?? 'certificate.pdf');
    } catch {
      notify.error('Failed to download that certificate.');
    }
  }

  async function downloadQrLabel(row: ItemInitRow) {
    // The label is keyed on the LT, so an unsaved lot has nothing to print.
    if (!row.lt) { notify.info('This lot has no LT yet, so there is no label to print.'); return; }
    try {
      // Legacy PREVIEWS the label in a new tab rather than saving it.
      if (!(await openLabelReport([row.oid]))) {
        notify.warning('Allow pop-ups for this site to view the QR label.');
      }
    } catch {
      notify.error('Failed to generate the QR label.');
    }
  }

  async function downloadCoa(row: ItemInitRow) {
    /* Legacy RETURNS SILENTLY when the lot has no certificate
       (PreviewCOA_Execute: `if (... Upload.Count == 0) return;`). One deliberate
       deviation: say so instead. The cell now reads "Attachment" on every row,
       exactly as GoLive does, so a silent click is indistinguishable from a
       broken button -- the same reasoning already recorded for the Reports menu
       (§18.6). */
    if (!row.fileDataOid) {
      notify.info('No certificate is attached to this lot.');
      return;
    }
    try {
      const res = await api.get(`/api/v1/item-init/file/${row.fileDataOid}/download`,
                                { responseType: 'blob' });
      downloadBlob(res.data, `COA-${row.lt ?? row.esid ?? 'certificate'}.pdf`);
    } catch {
      notify.error('Failed to download that certificate.');
    }
  }

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/item-init', { params: { take: ICM_LIST_TAKE } });
      const body = res.data as { items?: ItemInitRow[]; total?: number };
      const items = body.items ?? [];
      setRows(items);
      // Same uncapped fetch as Item Requisition -- 50 rows, silently.
      const note = icmTruncationNote(items.length, body.total);
      if (note) notify.info(note);
    } catch {
      setError('Failed to load item initializations.');
      setRows([]);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  const toArr = (d: unknown): unknown[] => Array.isArray(d) ? d : ((d as { data?: unknown[] })?.data ?? []);

  const loadItems = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/requisitions/items');
      const raw = toArr(res.data) as Array<{
        oid?: string; id?: string; itemName?: string; name?: string;
        catalog?: string | null; description?: string | null;
        defaultVendorId?: string | null; defaultVendorName?: string | null;
      }>;
      setItems(raw.map(i => ({
        id: i.oid ?? i.id ?? '',
        name: i.itemName ?? i.name ?? '',
        catalog: i.catalog ?? null,
        description: i.description ?? null,
        vendorId: i.defaultVendorId ?? null,
        vendorName: i.defaultVendorName ?? null,
      })));
    } catch {
      notify.error('Failed to load items lookup.');
    }
  }, [notify]);

  const loadLookups = useCallback(async () => {
    try {
      const [stRes, deptRes] = await Promise.all([
        api.get('/api/v1/distributions/storage-options'),
        api.get('/api/v1/system/departments'),
      ]);
      const mapStorage = toArr(stRes.data) as Array<{ oid?: string; storage?: string }>;
      setStorageOptions(mapStorage.map(s => ({ id: s.oid ?? '', name: s.storage ?? '' })));
      const mapDept = toArr(deptRes.data) as Array<{ oid?: string; id?: string; name?: string }>;
      setDepartments(mapDept.map(d => ({ id: d.oid ?? d.id ?? '', name: d.name ?? '' })));
    } catch {
      notify.error('Failed to load the storage / department lookups.');
    }
  }, [notify]);

  useEffect(() => { void load(); void loadItems(); void loadLookups(); }, [load, loadItems, loadLookups]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.lt ?? '').toLowerCase().includes(t) ||
      (r.esid ?? '').toLowerCase().includes(t) ||
      (r.itemName ?? '').toLowerCase().includes(t) ||
      (r.vendorLt ?? '').toLowerCase().includes(t)
    );
  }, [rows, search]);

  /* Column order mirrors Distribution_ListView_ItemInitialization: LT# first,
     then VendorLT, COA, Item, Vendor, Catalog#, Description, Storage,
     Department, Initialized Date, Initialized By, Expiry Date, Status. AI's own
     ESID, Given To and Total Items follow at the end rather than being dropped —
     legacy has no such columns, but the data is real and used elsewhere. */
  /** COA tab columns — the file name opens it, plus a per-row Delete, matching
   *  legacy's New · Delete · Download bar (Download is the file-name link). */
  const coaCols: AppColumn<VendorCoaAttachment>[] = [
    /* The download GLYPH column legacy draws between the tick box and the file
       name (owner 2026-09-30). Same target as the file-name link beside it —
       legacy has both, and both open the PDF. */
    { field: 'dl', header: '', width: 40, sortable: false, filterable: false, text: () => '',
      render: r => (
        <button type="button" className="adg-rowact" title="Download this certificate"
                onClick={e => { e.stopPropagation(); void downloadCoaFile(r); }}>
          <i className="ti ti-file-type-pdf" />
        </button>) },
    { field: 'fileName', header: 'File', width: 420,
      text: r => r.fileName ?? '',
      render: r => (
        <button type="button" className="adg-celllink bl-link"
                title="Download this certificate"
                onClick={e => { e.stopPropagation(); void downloadCoaFile(r); }}>
          {r.fileName ?? '(unnamed)'}
        </button>) },
    { field: 'sizeBytes', header: 'Size', width: 110, type: 'number',
      text: r => (r.sizeBytes ? `${Math.max(1, Math.round(r.sizeBytes / 1024))} KB` : ''),
      value: r => r.sizeBytes },
  ];

  const cols: AppColumn<ItemInitRow>[] = [
    /* Per-row EDIT PENCIL, the narrow column legacy draws between the selection
       checkbox and LT# (owner 2026-09-30: "add the edit button as a column in
       the list view"). It opens the same detail the toolbar Edit and a row
       double-click open -- one row, one click, no ticking first.
       `adg-rowact` is the shared in-cell action style on <AppDataGrid> itself,
       NOT a class invented here: it keeps the row at the 26px standard, which a
       bare in-cell <button> does not (see the note on the rule). `text: () => ''`
       keeps the column out of exports and the column filter -- there is nothing
       to export or filter about a button, and `sortable: false` keeps the grid
       from offering a sort control on a column of identical buttons. */
    { field: 'edit', header: '', width: 44, text: () => '', sortable: false, filterable: false,
      render: r => (
        <button type="button" className="adg-rowact" title="Edit"
                onClick={e => { e.stopPropagation(); void openEditById(r.oid, 'edit'); }}>
          <i className="ti ti-edit" />
        </button>
      ) },
    { field: 'lt',          header: 'LT#',          width: 130, text: r => r.lt ?? '—', render: r => r.lt ?? '—' },
    { field: 'vendorLt',    header: 'VendorLT',     width: 130, text: r => r.vendorLt ?? '—', render: r => r.vendorLt ?? '—' },
    /* COA -- legacy's PreviewCOA_LT column. Its cell text is the ACTION'S
       CAPTION, "Attachment" (Designer.cs:72, PaintStyle = Caption), which is why
       GoLive shows the same word on EVERY row whether or not a certificate is
       attached; it is a button label, not a value. Matched here (owner
       2026-09-30), replacing the earlier "COA" / "—" rendering.
       PreviewCOA_Execute opens the first file of the row's
       VendorReagentCertificate.Upload collection -- which is what AI's
       /item-init/file/{oid}/download already serves.
       ⚠️ `bl-link` IS LOAD-BEARING, not decoration. The app-wide black-cell rule
       (theme/blackText.css) forces `var(--ai-ink)` on everything inside a `td`
       and outranks `.vcoa-dl-name`, so these rendered as plain dark text while
       GoLive shows blue links (owner 2026-09-30: "set the same color for the
       Attachment"). That rule carries an explicit opt-out list -- .bl-link /
       .mlv-link-blue / .dv-att-link -- which is the sanctioned way to say "this
       one IS a link"; both resolve to #1565c0. Using the existing mechanism
       rather than inventing a higher-specificity override (standing rule 8). */
    { field: 'fileDataOid', header: 'COA', width: 100,
      sortable: false, filterable: false, text: () => '',
      render: r => (
        <button type="button" className="adg-celllink bl-link"
                title={r.fileDataOid
                  ? 'Open the vendor certificate'
                  : 'No certificate is attached to this lot'}
                onClick={e => { e.stopPropagation(); void downloadCoa(r); }}>
          Attachment
        </button>) },
    /* QRLabel -- legacy's PreviewQRLabel column (VendorReagentCertificateViewController
       :676). ⚠️ It is NOT a stored attachment despite the cell reading
       "Attachment" there: legacy renders the DevExpress report layout "LTBarcode"
       for the row's LT and opens the PDF in a new tab. AI already has that
       renderer -- IIcmLtLabelPdfGenerator behind
       POST /api/v1/icm-tracking/lt-coa/label-report, the same one LT# COA
       Tracking's inline Label button uses -- so this reuses it rather than adding
       a second (standing rule 8).
       The cell text is "Attachment" like legacy's, for the same reason as the COA
       column above: it is the action's caption, not data. (It read "Label" for
       one build; corrected on the owner's side-by-side 2026-09-30.) */
    { field: 'qrLabel', header: 'QRLabel', width: 100, text: () => '',
      sortable: false, filterable: false,
      render: r => (
        <button type="button" className="adg-celllink bl-link"
                title={r.lt ? `QR label for ${r.lt}` : 'This lot has no LT yet'}
                onClick={e => { e.stopPropagation(); void downloadQrLabel(r); }}>
          Attachment
        </button>) },
    { field: 'itemName',    header: 'Item',         width: 220, text: r => r.itemName ?? '—', render: r => r.itemName ?? '—' },
    { field: 'vendorName',  header: 'Vendor',       width: 160, text: r => r.vendorName ?? '—', render: r => r.vendorName ?? '—' },
    { field: 'catalog',     header: 'Catalog#',     width: 130, text: r => r.catalog ?? '—', render: r => r.catalog ?? '—' },
    { field: 'description', header: 'Description',  width: 220, text: r => r.description ?? '—', render: r => r.description ?? '—' },
    { field: 'storageName', header: 'Storage',      width: 140, text: r => r.storageName ?? '—', render: r => r.storageName ?? '—' },
    { field: 'departmentName', header: 'Department', width: 140, text: r => r.departmentName ?? '—', render: r => r.departmentName ?? '—' },
    { field: 'initializedDate', header: 'Initialized Date', width: 150,
      text: r => fmtLocalShort(r.initializedDate) ?? '—', render: r => fmtLocalShort(r.initializedDate) ?? '—' },
    { field: 'initializedByName', header: 'Initialized By', width: 150,
      text: r => r.initializedByName ?? '—', render: r => r.initializedByName ?? '—' },
    { field: 'expiryDate',  header: 'Expiry Date',  width: 120, text: r => fmtLocalDate(r.expiryDate) ?? '—', render: r => fmtLocalDate(r.expiryDate) ?? '—' },
    { field: 'statusLabel', header: 'Status',       width: 130, type: 'status', text: r => r.statusLabel },
    /* No "Given To" column: the field left this form with the 2026-09-29 layout
       change, so it can only ever be blank for a lot created here. */
  ];

  function openNew() {
    loadedForm.current = EMPTY_FORM;
    setForm(EMPTY_FORM);
    setRo(emptyRo(meName)); setCertId(null); setCoaFiles([]);
    setDetail({ mode: 'new' });
  }

  const openEditById = useCallback(async (id: string, mode: 'edit' | 'view' = 'view') => {
    try {
      const res = await api.get(`/api/v1/item-init/${id}`);
      const d = (res.data as { data?: Record<string, unknown> })?.data ?? res.data as Record<string, unknown>;
      const str = (v: unknown) => (v == null ? '' : String(v));
      const loaded: InitForm = {
        itemId: str(d.itemId),
        storageId: str(d.storageId),
        departmentId: str(d.departmentId),
        // Order Qty is the form's one quantity; fall back to the stored
        // TotalItems for a lot written before the two were unified.
        orderQty: Number(d.orderQty ?? d.totalItems ?? 1),
        receiveDate: str(d.receiveDate).substring(0, 10),
        expiryDate: str(d.expiryDate).substring(0, 10),
        vendorLt: str(d.vendorLt),
        comment: str(d.comment),
      };
      setForm(loaded);
      loadedForm.current = loaded;
      const cert = (d.vendorReagentCertificateId as string) ?? null;
      setCertId(cert);
      void loadCoaFiles(cert);
      setRo({
        lt: (d.lt as string) ?? null,
        statusLabel: (d.statusLabel as string) ?? '',
        initializedDate: (d.initializedDate as string) ?? null,
        initializedByName: (d.initializedByName as string) ?? null,
        vendorName: (d.vendorName as string) ?? null,
        // Legacy binds Description to the ITEM's Specification, not to the
        // lot's own snapshot; fall back to the snapshot when it is blank.
        description: (d.itemSpecification as string) ?? (d.description as string) ?? null,
        catalog: (d.catalog as string) ?? null,
      });
      setDetail({ mode, id });
    } catch {
      notify.error('Failed to load record for editing.');
    }
  }, [notify]);

  async function openEdit() {
    if (selected.size !== 1) { notify.warning('Select one row to edit.'); return; }
    await openEditById(String(Array.from(selected)[0]!), 'edit');
  }

  async function deleteSelected() {
    if (selected.size === 0) { notify.warning('Select at least one row to delete.'); return; }
    const ids = Array.from(selected);
    if (!(await confirm({ title: 'Delete?', message: `Delete ${ids.length} initialization record(s)?`, tone: 'danger', confirmLabel: 'Delete' }))) return;
    try {
      await Promise.all(ids.map(id => api.delete(`/api/v1/item-init/${id}`)));
      setSelected(new Set());
      await load();
      notify.success('Deleted.');
    } catch {
      notify.error('Delete failed.');
    }
  }

  /** The item drives Vendor / Description / Catalog#, which legacy shows
   *  read-only beside it — so picking one refreshes all three at once. */
  /* The Item field is legacy's multi-column GRID LOOKUP (ASPxGridLookup), not a
     plain dropdown: GoLive lists Item · Vendor · Description · Catalog# with a
     filter row under the captions, because an item name alone does not identify
     a lot -- "6020 Interference Check" appears twice in this data and the vendor
     and catalog number are what tell them apart (owner 2026-09-30: "include
     these data Vendor, Description and Catalog for the Item field").
     The shared <LookupCombo> IS that control (standing rule 8) -- the same one
     Sample Sites' Client and Maintenance Setup's Instrument ID use. No API or SP
     change was needed: the options endpoint already returns vendor, description
     and catalog, which is how the read-only fields below auto-fill.
     Column 0 is the display value, and the id is mapped back per ROW rather than
     by name, since item names repeat. */
  const itemLookup = useMemo(
    () => ({
      cols: ['Item', 'Vendor', 'Description', 'Catalog#'],
      rows: items.map(i => [i.name, i.vendorName ?? '', i.description ?? '', i.catalog ?? '']),
    }),
    [items],
  );
  /** Picked row -> item id. Keyed on the row ARRAY identity (the same arrays the
   *  lookup above holds), so two items with the same name stay distinguishable. */
  const itemIdByRow = useMemo(() => {
    const m = new Map<string[], string>();
    itemLookup.rows.forEach((cells, n) => m.set(cells, items[n]!.id));
    return m;
  }, [itemLookup, items]);
  const selectedItemName = items.find(i => i.id === form.itemId)?.name ?? '';

  function pickItem(itemId: string) {
    const it = items.find(i => i.id === itemId);
    setForm(f => ({ ...f, itemId }));
    setRo(r => ({
      ...r,
      vendorName: it?.vendorName ?? null,
      description: it?.description ?? null,
      catalog: it?.catalog ?? null,
    }));
  }

  const qty = form.orderQty === '' ? 0 : Number(form.orderQty);
  /* Required set (owner 2026-09-30). ⚠️ Vendor Lot, Expiry Date and Department
     go BEYOND legacy -- Distribution.cs carries no [RuleRequiredField] on any of
     the three (its only such rule is commented out, on Vendor) -- so this is an
     AI tightening, not a port. The SERVER enforces the same three
     (AI_ICM_ItemInit_Save_SP, gated on @ItemInitInteractive so the Excel import
     stays exempt); this is the UX half (§14b.5). */
  const missing = !form.itemId || !form.storageId || !(qty > 0)
    || !form.vendorLt.trim() || !form.expiryDate || !form.departmentId;

  /** after = what the Save trio does once the record is persisted. */
  async function handleSave(after: 'stay' | 'new' | 'close') {
    if (!detail) return;
    if (missing) {
      // Name the fields that are actually empty, so the user is not left
      // hunting a form with six required controls on it.
      const gaps = [
        !form.itemId && 'Item', !(qty > 0) && 'Order Qty',
        !form.vendorLt.trim() && 'Vendor Lot', !form.expiryDate && 'Expiry Date',
        !form.storageId && 'Storage', !form.departmentId && 'Department',
      ].filter(Boolean) as string[];
      notify.warning(`${gaps.join(', ')} ${gaps.length === 1 ? 'is' : 'are'} required.`);
      return;
    }
    const it = items.find(i => i.id === form.itemId);
    const payload = {
      itemId:       form.itemId,
      vendorId:     it?.vendorId || null,
      storageId:    form.storageId,
      // Legacy files an initialized lot under a Storage and leaves givento NULL
      // (ExistingstockViewController ~1025); only the hand-to-a-person variant
      // sets it, and this screen is not that.
      givenToId:    null,
      // "" cannot bind to Guid?/int?/DateTime? — the framework would reject the
      // request before the SP's own messages could be reached.
      departmentId: form.departmentId || null,
      orderQty:     qty,
      // Order Qty IS the quantity on this form; TotalItems is what the SP uses
      // to move itemremaining and the item's stock, so they are the same number.
      totalItems:   qty,
      receiveDate:  form.receiveDate || null,
      expiryDate:   form.expiryDate || null,
      vendorLt:     form.vendorLt,
      comment:      form.comment,
    };
    /* The id a NEW save mints, so the "stay" branch can re-read it. `detail`
       is captured by this closure, so switching it inside `save` does NOT make
       the re-read below see the new record -- which is why Save (stay) on a new
       record used to leave the header without its LT and the server-stamped
       Status / Initialized fields blank. */
    let savedId = detail.id ?? null;
    /* The lot number the SP mints, shown in the save acknowledgement (owner
       2026-09-30: "display the ID while save"). §18.6 allows a MILESTONE-SUCCESS
       DIALOG to carry a new record's id -- a toast may not -- which is the same
       route Ordering Items uses for its PO # and Receiving Items for its
       Receive #. Assigned inside `save` and read by `successDialog`, which
       useSaveFlow evaluates AFTER the save resolves. */
    let mintedLt: string | null = null;
    /* EVERY lot the save minted. A receipt of N containers is N rows with N
       distinct LTs (AI_ICM_ItemInit_Save_SP Step 4), so the acknowledgement
       names the batch, not just the first bottle. */
    let mintedLts: string[] = [];
    setSaving(true);
    try {
      await saveFlow({
        formName: 'ICM Item Init',
        auditAction: detail.mode === 'new' ? 'create' : 'edit',
        save: async () => {
          if (detail.mode === 'new') {
            const res = await api.post('/api/v1/item-init', payload);
            const body = res.data as { id?: string; lt?: string; lts?: string[] } | undefined;
            const id = body?.id ?? null;
            mintedLt = body?.lt ?? null;
            mintedLts = body?.lts ?? (body?.lt ? [body.lt] : []);
            savedId = id;
            if (after === 'stay' && id) setDetail({ mode: 'edit', id });
          } else {
            await api.put(`/api/v1/item-init/${detail.id}`, payload);
          }
        },
        /* GoLive's own post-save wording, CORRECTED 2026-09-30. This used to
           read "Saved successfully.", sourced from ExistingstockViewController
           -- but that is the Existing Stock screen, not this one. Item
           Initialization's detail view (Distribution_DetailView_ItemInitialization)
           is served by DistributionViewController, whose SaveInitialization()
           ends with its OWN explicit messages:
             create -> $"{OrderQty} LT{(OrderQty > 1 ? "s" : "")} generated successfully."
             edit   -> "Updated successfully."
           Both are reproduced verbatim. §18.6 allows a MILESTONE-SUCCESS DIALOG
           to carry new record ids -- a toast may not -- which is the same route
           Ordering Items uses for its PO # and Receiving Items for its Receive #.
           For a multi-container receipt the id line shows the RANGE rather than
           a thousand-entry list; the count row says how many there are. */
        successDialog: detail.mode === 'new'
          ? () => {
              const n = mintedLts.length || qty;
              const first = mintedLts[0] ?? mintedLt ?? '';
              const last  = mintedLts[mintedLts.length - 1] ?? first;
              return {
                type: 'success' as const,
                title: `${n} LT${n === 1 ? '' : 's'} generated successfully.`,
                idLabel: n === 1 ? 'LT #' : 'LT # range',
                idValue: n === 1 ? first : `${first} – ${last}`,
                rows: [
                  { label: 'Item',       value: items.find(i => i.id === form.itemId)?.name || '—' },
                  { label: 'Containers', value: String(n) },
                  { label: 'Storage',    value: storageOptions.find(o => o.id === form.storageId)?.name || '—' },
                ],
              };
            }
          : () => ({
              type: 'success' as const,
              title: 'Updated successfully.',
              idLabel: 'LT #',
              idValue: ro.lt || '',
              rows: [],
            }),
        onError: (err: unknown) =>
          (err as { response?: { data?: { error?: { message?: string } } } })
            ?.response?.data?.error?.message,
        onSaved: async () => {
          await load();
          if (after === 'close') setDetail(null);
          else if (after === 'new') { loadedForm.current = EMPTY_FORM; setForm(EMPTY_FORM); setRo(emptyRo(meName)); setCertId(null); setCoaFiles([]); setDetail({ mode: 'new' }); }
          // Staying on the record: re-read it so the minted LT and everything
          // the SERVER stamped (Status, Initialized Date / By) are the stored
          // values, not the optimistic ones this form guessed.
          else if (savedId) await openEditById(savedId, 'edit');
        },
      });
    } finally { setSaving(false); }
  }



  /** Prev / next RECORD, the arrows the shared <SettingsDetailPage> puts in its
   *  header (standing rule 12). They walk the ids of the list behind the detail,
   *  in the order the grid is showing them, so "next" means what the user can
   *  see. Both are null on a NEW record (nothing to step through) and at the
   *  ends, which disables the buttons rather than wrapping around.
   *
   *  Stepping away discards an edited form, so it asks first with the ONE shared
   *  wording (standing rule 15). */
  const siblingIds = rows.map(r => r.oid);
  const recIdx     = detail?.id ? siblingIds.indexOf(detail.id) : -1;
  const prevRecId  = recIdx > 0 ? siblingIds[recIdx - 1]! : null;
  const nextRecId  = recIdx >= 0 && recIdx < siblingIds.length - 1 ? siblingIds[recIdx + 1]! : null;

  async function goToRecord(id: string) {
    const edited = JSON.stringify(form) !== JSON.stringify(loadedForm.current);
    if (edited && !(await confirm({
      title: 'Leave this record?',
      message: 'Unsaved changes will be lost. Do you want to proceed?',
    }))) return;
    await openEditById(id, detail?.mode === 'view' ? 'view' : 'edit');
  }

  function setF<K extends keyof InitForm>(k: K, v: InitForm[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  /* ── The full-page detail view (legacy Distribution_DetailView_ItemInitialization).
        Two columns, labels beside their fields, the action bar repeated top and
        bottom — the shared <SettingsDetailPage> anatomy (standing rule 12). */
  if (detail) {
    /* Same name and meaning as the shared page's flag, so the two read alike. */
    const viewOnly = detail.mode === 'view';
    const title = detail.mode === 'new'
      ? 'Item Initialization – New'
      : `Item Initialization – ${ro.lt ?? ''}`;

    /* The shared <SettingsDetailPage> bar, button for button: its glyphs, its
       wording ("Save and Close", not "Save & Close") and its set --
       Save . Save and Close . Save and New . | . Cancel.
       ⚠️ DELETE, REFRESH AND AUDIT TRAIL WERE REMOVED 2026-09-30 (owner: "could
       you remove other controls", comparing against GoLive). They had been added
       here on 2026-09-29 on the reading that legacy's detail carries them -- it
       does, but on its own PAGE toolbar above the title, NOT in the save bar.
       Measured on the reference form (Settings → Inventory Management → Items,
       CLAUDE.md rule 12): its detail bar is these FOUR buttons and nothing else,
       on a NEW record AND on an existing one. This form now matches.
       Nothing is lost: Delete and Refresh are on the LIST toolbar, which is
       where the reference keeps them, and the audit log still surfaces from the
       list's `auditFormName="Item Initialization"` (standing rule 11).
       The SAVE TRIO is disabled only while a save is IN FLIGHT -- never because
       a required field is still empty (owner 2026-09-30: "enable all the form
       controls"). That is the shared <SettingsDetailPage> contract: its Save
       buttons are `disabled={saving || loading}` and required fields are checked
       when the button is PRESSED, showing the message + the yellow
       `required-empty` highlight on the offending fields (§18.6). This form was
       the only one in the app that greyed Save out instead, which read as a dead
       toolbar on a brand-new record; `handleSave` already carried the same
       click-time guard, so the disable was redundant as well as non-standard. */
    /* VIEW mode carries the shared page's own view bar -- Edit, then Close --
       and no Save set, because there is nothing to save until the user asks to
       edit (SettingsDetailPage.tsx:1013-1038). Delete stays off it for the same
       reason it is off the edit bar: the reference form keeps Delete on the
       LIST. */
    const actions = (where: 'top' | 'bottom') => (
      <div className={`sd-actionbar${where === 'bottom' ? ' bottom' : ''}`}>
        {viewOnly ? (<>
          <button className="sd-abtn primary" disabled={saving}
                  onClick={() => setDetail(d => (d ? { ...d, mode: 'edit' } : d))}>
            <span className="tb-i tb-i-blue">✎</span> Edit
          </button>
          <span className="sd-abtn-sep" aria-hidden="true" />
          <button className="sd-abtn cancel" disabled={saving} onClick={() => setDetail(null)}>
            <span className="tb-i">↩︎</span> Close
          </button>
        </>) : (<>
        <button className="sd-abtn primary" disabled={saving} onClick={() => void handleSave('stay')}>
          <span className="tb-i">💾</span> Save
        </button>
        <button className="sd-abtn" disabled={saving} onClick={() => void handleSave('close')}>
          <span className="tb-i">💾</span> Save and Close
        </button>
        <button className="sd-abtn" disabled={saving} onClick={() => void handleSave('new')}>
          <span className="tb-i">📄</span> Save and New
        </button>
        <span className="sd-abtn-sep" aria-hidden="true" />
        <button className="sd-abtn cancel" disabled={saving} onClick={() => setDetail(null)}>
          <span className="tb-i">↩︎</span> Cancel
        </button>
        </>)}
      </div>
    );

    return (
      /* The SAME page chrome the reference form uses -- Settings → Inventory
         Management → Items renders .bl-page > .bl-grid-area > .sd-wrap, and
         .bl-grid-area is what supplies the 20px side gutter and 16px bottom
         padding (CLAUDE.md rule 12). This detail hung straight off `.content`,
         so it sat flush to the page edges and measured 40px wider than the
         reference at the same viewport. The LIST view below already wraps this
         way -- only the detail did not. */
      <div className="bl-page">
       <div className="bl-grid-area">
        {/* The shared List View / Detail View switch, in the same place and with
            the same component the reference form uses (ModuleListView renders
            <ViewToggle> immediately above the detail card). "List View" is the
            way back, alongside the breadcrumb and Cancel. */}
        <div style={{ padding: '10px 0 0' }}>
          <ViewToggle view="detail" onChange={v => { if (v === 'list') setDetail(null); }} />
        </div>
        <div className="sd-wrap">
        {/* Header anatomy copied from the reference form, which nests the TITLE
            and the breadcrumb in one block -- h2 FIRST, breadcrumb beneath --
            with the record arrows as the block's sibling so `margin-left:auto`
            pushes them to the right end. This form had the two the other way
            round and side by side, because `.sd-head` is `display:flex` and they
            were direct children of it. The breadcrumb is rooted at Home like the
            reference's (Home > Settings > Items > …) and like this form's own
            list view. */}
        <div className="sd-head">
          <div>
            <h2>{title}</h2>
            <div className="sd-bc">
              <a onClick={() => setDetail(null)}>Home</a>
              <i className="ti ti-chevron-right" />
              <span>ICM Operations</span>
              <i className="ti ti-chevron-right" />
              <a onClick={() => setDetail(null)}>Item Initialization</a>
              <i className="ti ti-chevron-right" />
              <span>{detail.mode === 'new' ? 'New' : (ro.lt ?? 'Edit')}</span>
            </div>
          </div>
          {/* The shared header's prev/next RECORD arrows (standing rule 12).
              Same markup and same `.sd-navarrows` / `.sd-na` classes the shared
              <SettingsDetailPage> uses, so they look and behave identically --
              `.sd-head` is already `display:flex` and `.sd-navarrows` carries
              `margin-left:auto`, so they sit at the right end of the header. */}
          <div className="sd-navarrows">
            <button className="sd-na" title="Previous record"
                    disabled={!prevRecId || saving}
                    onClick={() => prevRecId && void goToRecord(prevRecId)}>‹</button>
            <button className="sd-na next" title="Next record"
                    disabled={!nextRecId || saving}
                    onClick={() => nextRecId && void goToRecord(nextRecId)}>›</button>
          </div>
        </div>

        {actions('top')}

        <div className="sd-body">
          {/* The shared detail layout (ModuleCrudConfig.sideLabels + columnMajor,
              standing rule 12): ONE `.sd-grid` whose `--sd-rows` splits it into a
              left and a right column filled top-to-bottom, a fixed left-aligned
              label column, and every control a uniform 26px. It replaces the
              bespoke two-grid `.ii-cols` this page shipped with earlier today,
              which stretched the controls across the whole page and sized its
              rows differently from every other detail form.
              The two columns are legacy's layout groups Item1 and Item2.
              --sd-rows is the length of the TALLER column (the right one, 8),
              NOT of the left: `grid-auto-flow: column` fills exactly that many
              rows before wrapping, so a smaller number spills the overflow into
              a THIRD implicit column and squeezes every control (seen on screen
              with 7). The left group has only 7 fields, so its tall Comment
              spans two rows (`sd-row--span2`) to keep the split at legacy's
              7 | 8 instead of letting Order Qty wrap into the left column. */}
          <div className="sd-grid sd-grid--colmajor sd-grid--side"
               style={{ ['--sd-rows' as string]: '8' }}>
            {/* LEFT -- legacy layout group "Item1" */}
            <label className="sd-row">
              <span className="sd-label">LT</span>
              <input className="sd-input readonly" type="text" readOnly
                     value={ro.lt ?? ''} placeholder="Auto generate" />
            </label>
            {/* VIEW mode renders the three REFERENCE fields (Item, Storage,
                Department) as links to the forms that own them, which is what
                GoLive does: its Distribution_DetailView_ItemInitialization at
                &mode=View draws each bound reference as a hyperlink, so a user
                reading "1000 ppm Lithium" clicks through to Items_DetailView.
                EDIT mode keeps the editors -- legacy's own View/Edit split.
                Via the shared <FormLink> (standing rule 8), so the permission
                gate, the in-app navigation and the unsaved-changes prompt are
                the ones every other linked value already uses. */}
            {viewOnly ? (
              <div className="sd-row">
                <span className="sd-label">Item<span className="req">*</span></span>
                <FormLink to="set-inventory-items" recordId={form.itemId}
                          value={selectedItemName} className="sd-linkvalue" />
              </div>
            ) : (
            <label className="sd-row">
              <span className="sd-label">Item<span className="req">*</span></span>
              <LookupCombo
                lookup={itemLookup}
                disabled={viewOnly}
                value={selectedItemName}
                placeholder="&mdash; select &mdash;"
                popupWidth={620}
                columnFilters
                className={form.itemId ? undefined : 'required-empty'}
                onPick={row => pickItem(itemIdByRow.get(row) ?? '')}
              />
            </label>
            )}
            <label className="sd-row">
              <span className="sd-label">Vendor</span>
              <input className="sd-input readonly" type="text" readOnly value={ro.vendorName ?? ''} />
            </label>
            <label className="sd-row">
              <span className="sd-label">Description</span>
              <input className="sd-input readonly" type="text" readOnly value={ro.description ?? ''} />
            </label>
            <label className="sd-row">
              <span className="sd-label">Catalog#</span>
              <input className="sd-input readonly" type="text" readOnly value={ro.catalog ?? ''} />
            </label>
            <label className="sd-row">
              <span className="sd-label">Vendor Lot<span className="req">*</span></span>
              <input className={`sd-input${viewOnly ? ' readonly' : (form.vendorLt.trim() ? '' : ' required-empty')}`}
                     type="text" value={form.vendorLt} readOnly={viewOnly}
                     onChange={e => setF('vendorLt', e.target.value)} />
            </label>
            <label className="sd-row sd-row--span2">
              <span className="sd-label">Comment</span>
              {/* `sd-input--tall` = the shared opt-in multi-line height (owner
                  2026-09-30: "increase the height of the comment"). A shared
                  modifier rather than a height on this one control, so the next
                  form that needs a real comment box asks for the same thing
                  instead of inventing its own (standing rule 8). Still
                  drag-resizable. */}
              <textarea className={`sd-input sd-input--tall${viewOnly ? ' readonly' : ''}`}
                        value={form.comment} readOnly={viewOnly}
                        onChange={e => setF('comment', e.target.value)} />
            </label>

            {/* No spacer cell here any more: the Comment above carries
                `sd-row--span2`, so it occupies rows 7 AND 8 of this column and
                pads the group to --sd-rows by itself. The old empty <div> left
                the tall Comment on row 7, which made that row 72px for BOTH
                columns and opened a visible hole under Initialized Date. */}

            {/* RIGHT -- legacy layout group "Item2" */}
            <label className="sd-row">
              <span className="sd-label">Order Qty<span className="req">*</span></span>
              <input className={`sd-input${viewOnly ? ' readonly' : (qty > 0 ? '' : ' required-empty')}`}
                     type="number" min={1} value={form.orderQty} readOnly={viewOnly}
                     onChange={e => setF('orderQty', e.target.value === '' ? '' : Number(e.target.value))} />
            </label>
            <label className="sd-row">
              <span className="sd-label">Received Date</span>
              <input className={`sd-input${viewOnly ? ' readonly' : ''}`} type="date"
                     value={form.receiveDate} readOnly={viewOnly}
                     onChange={e => setF('receiveDate', e.target.value)} />
            </label>
            <label className="sd-row">
              <span className="sd-label">Expiry Date<span className="req">*</span></span>
              <input className={`sd-input${viewOnly ? ' readonly' : (form.expiryDate ? '' : ' required-empty')}`}
                     type="date" value={form.expiryDate} readOnly={viewOnly}
                     onChange={e => setF('expiryDate', e.target.value)} />
            </label>
            {viewOnly ? (
              /* set-icm-storage, NOT set-storage: this form's Storage is
                 dbo.ICMStorage (Settings > Utility > Inventory Management >
                 Storage), which is the list AI_ICM_ItemInit_Save_SP resolves
                 against. set-storage is the unrelated Test/Analysis master. */
              <div className="sd-row">
                <span className="sd-label">Storage<span className="req">*</span></span>
                <FormLink to="set-icm-storage" className="sd-linkvalue" recordId={form.storageId}
                          value={storageOptions.find(o => o.id === form.storageId)?.name} />
              </div>
            ) : (
            <label className="sd-row">
              <span className="sd-label">Storage<span className="req">*</span></span>
              <select className={`sd-input${viewOnly ? ' readonly' : (form.storageId ? '' : ' required-empty')}`}
                      disabled={viewOnly}
                      value={form.storageId} onChange={e => setF('storageId', e.target.value)}>
                <option value="">&mdash; select &mdash;</option>
                {storageOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
            )}
            {viewOnly ? (
              <div className="sd-row">
                <span className="sd-label">Department<span className="req">*</span></span>
                <FormLink to="sm-department" className="sd-linkvalue" recordId={form.departmentId}
                          value={departments.find(o => o.id === form.departmentId)?.name} />
              </div>
            ) : (
            <label className="sd-row">
              <span className="sd-label">Department<span className="req">*</span></span>
              <select className={`sd-input${viewOnly ? ' readonly' : (form.departmentId ? '' : ' required-empty')}`}
                      disabled={viewOnly}
                      value={form.departmentId}
                      onChange={e => setF('departmentId', e.target.value)}>
                <option value="">&mdash; select &mdash;</option>
                {departments.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
            )}
            <label className="sd-row">
              <span className="sd-label">Status</span>
              <input className="sd-input readonly" type="text" readOnly value={ro.statusLabel} />
            </label>
            <label className="sd-row">
              <span className="sd-label">Initialized Date</span>
              <input className="sd-input readonly" type="text" readOnly
                     value={fmtLocalShort(ro.initializedDate) ?? ''} />
            </label>
            <label className="sd-row">
              <span className="sd-label">Initialized By</span>
              <input className="sd-input readonly" type="text" readOnly value={ro.initializedByName ?? ''} />
            </label>
          </div>

          {/* ── COA tab ──────────────────────────────────────────────────────
              Legacy's Distribution_DetailView_ItemInitialization carries one
              tab, "COA", whose child grid lists the lot's certificate files with
              New · Delete · Download. Drawn with the SHARED child-tab chrome
              (`sd-assoc-tabbar` + `sd-assoc-tab.active` over `sd-childtabpanel`)
              — the same component Items renders "Sub Item" with and Vendors COA
              renders its Upload tab with, so all three read identically.
              Hidden on a NEW record: the lot has no certificate until it is
              saved, so there is nothing to attach a file to — legacy's tab is
              likewise empty there. */}
          {detail.mode !== 'new' && (
            <>
              <div className="sd-assoc-tabbar" role="tablist" style={{ marginTop: 18 }}>
                <button type="button" role="tab" aria-selected className="sd-assoc-tab active">
                  COA
                </button>
              </div>
              <div className="sd-childtabpanel" role="tabpanel">
                {/* Legacy's child bar, button for button: New · Delete · Download.
                    Delete and Download act on the TICKED rows. */}
                <div className="sd-childbar">
                  {/* Enabled for any SAVED lot: a missing certificate is no longer a
                      reason to refuse, because uploadCoaFile creates one first.
                      Only an unsaved (New) record has nothing to attach to. */}
                  <label className={`ca-btn${coaBusy || !detail?.id ? ' disabled' : ''}`}
                         title={detail?.id ? 'Attach a certificate PDF'
                                           : 'Save this lot before attaching a certificate'}>
                    <i className="ti ti-plus" /> New
                    <input type="file" accept="application/pdf" hidden
                           disabled={coaBusy || !detail?.id}
                           onChange={e => {
                             const f = e.target.files?.[0];
                             e.target.value = '';
                             if (f) void uploadCoaFile(f);
                           }} />
                  </label>
                  <button type="button" className="ca-btn"
                          disabled={coaBusy || coaSel.size === 0}
                          onClick={() => void deleteCoaSelection()}>
                    <i className="ti ti-trash" /> Delete
                  </button>
                  <button type="button" className="ca-btn"
                          disabled={coaBusy || coaSel.size === 0}
                          onClick={() => void downloadCoaSelection()}>
                    <i className="ti ti-download" /> Download
                  </button>
                </div>
                <AppDataGrid<VendorCoaAttachment>
                  rows={coaFiles}
                  columns={coaCols}
                  getRowId={r => r.oid}
                  selectable
                  selectedIds={coaSel}
                  onSelectionChange={setCoaSelFromGrid}
                  emptyText="No certificate attached."
                  maximizable
                  exportFilename="item-init-coa"
                />
              </div>
            </>
          )}
        </div>

        {actions('bottom')}
        </div>
       </div>
      </div>
    );
  }

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>ICM Operations</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Item Initialization</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="icm-item-init" title="Item Initialization"
                       auditFormName="Item Initialization"
                       loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <>
                {/* IMPORT + DOWNLOAD TEMPLATE -- legacy's Import_Execute and
                    DownloadTemplate_Execute on this view. The workbook columns are
                    VendorLT . ItemName . Vendor . Catelog# . Description . Qty .
                    Storage . Department . ExpiryDate . ReceivedDate. */}
                <label className="ca-btn" htmlFor="ii-import"
                       style={{ cursor: importing ? 'wait' : 'pointer' }}
                       title="Import initialized stock from the Excel template">
                  <i className="ti ti-file-import" /> {importing ? 'Importing…' : 'Import'}
                </label>
                <input id="ii-import" type="file" hidden disabled={importing}
                       accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                       onChange={e => { void importFile(e.target.files); e.target.value = ''; }} />
                <button type="button" className="ca-btn" style={{ marginLeft: 6 }}
                        onClick={() => void downloadTemplate()}
                        title="Download the Item Initialization import template">
                  <i className="ti ti-download" /> Download Template
                </button>
              </>
            }
            /* Legacy's bar is New · Edit · Delete · Import · Download Template ·
               Refresh · Export to · Audit Trail · Search, so the CRUD group
               comes BEFORE the form-specific pair rather than after it
               (owner request 2026-09-29). The shared opt-in, never a hand-rolled
               reorder -- the buttons keep their glyphs and Alt+N / Alt+E / Alt+D. */
            crudFirst
            onNew={openNew}
            onEdit={openEdit}
            editDisabled={selected.size !== 1}
            onDelete={deleteSelected}
            deleteDisabled={selected.size === 0}
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: 'item-init', ...gridExportMatrix(filtered, cols) }}
          />
        </div>
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search LT, item, vendor…" />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
        </div>
        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
          ) : (
            <AppDataGrid<ItemInitRow>
              rows={filtered}
              columns={cols}
              getRowId={r => r.oid}
              loading={loading}
              loadMs={loadMs}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              onRowDoubleClick={r => void openEditById(r.oid, 'view')}
              emptyText="No item initializations found."
            />
          )}
        </div>
      </div>
    </div>
  );
}
